// SourceDetailV4Page — table-first, backend-derived. No V3 references.
//
// Layout: one thin top bar with `<name> <id> [Actions ▾]`, rest of the
// viewport is the events:preview table. Every backend mutation /
// inspection lives behind the Actions dropdown as a flat list grouped
// by HTTP verb. Each entry corresponds 1:1 to a real route under
// /api/v1/sources/:id — nothing invented, nothing curated.
//
// Click on an endpoint opens a centered modal:
//   - GET with no params  → fires immediately, shows raw JSON response
//   - everything else     → renders a minimal form: text input per path
//                           param, one JSON textarea for body (if any),
//                           [Run] button, raw JSON response below
//
// No metrics strip, identity strip, toolbar, drawer, tabs, snippets,
// templates, fingerprint chips. No oklch, no var(--*), no V3 widgets.

import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import type { DataHubEvent } from '@datahub/shared';
import { TagChip } from '../lib/tag-colors';
import { NestedCell, readPath } from '../lib/nested-cell';

interface SchemaField {
  path: string;
  depth?: number;
  parent?: string | null;
  occurrences: number;
  collapsed_cardinality?: number;
  parent_kind?: 'object' | 'array' | 'scalar' | 'null';
  sparse?: boolean;
}

// ── Endpoint inventory ────────────────────────────────────────────
// Hand-generated from grep -rE "\.(get|post|patch|delete)\(" routes/
// (49 endpoints, every source-scoped route as of 2026-05-11).
// `param` lists extra path params beyond :id; `body` true means a
// JSON body input is rendered; `query` lists optional query keys.

type Method = 'GET' | 'POST' | 'PATCH' | 'DELETE';
type Endpoint = {
  method: Method;
  path: string;            // displayed path with :id substituted at run time
  params?: string[];       // extra path params beyond :id (each rendered as text input)
  body?: boolean;          // POST/PATCH render a JSON textarea
  query?: string[];        // optional query params, rendered as text inputs
};

const ENDPOINTS: Endpoint[] = [
  // ── GET ─────────────────────────────────────────────────────────
  { method: 'GET', path: '/api/v1/sources/:id' },
  { method: 'GET', path: '/api/v1/sources/:id/bq-views' },
  { method: 'GET', path: '/api/v1/sources/:id/dedup-analysis' },
  { method: 'GET', path: '/api/v1/sources/:id/events:count' },
  { method: 'GET', path: '/api/v1/sources/:id/events:preview', query: ['limit', 'before', 'include_superseded'] },
  { method: 'GET', path: '/api/v1/sources/:id/events:schema', query: ['group_by'] },
  { method: 'GET', path: '/api/v1/sources/:id/events/:eventId/lineage', params: ['eventId'] },
  { method: 'GET', path: '/api/v1/sources/:id/members' },
  { method: 'GET', path: '/api/v1/sources/:id/notifications', query: ['include_acknowledged'] },
  { method: 'GET', path: '/api/v1/sources/:id/raw_uploads' },
  { method: 'GET', path: '/api/v1/sources/:id/raw_uploads/:uploadId/download', params: ['uploadId'] },
  { method: 'GET', path: '/api/v1/sources/:id/raw_uploads/:uploadId/signed-url', params: ['uploadId'] },
  { method: 'GET', path: '/api/v1/sources/:id/replays' },
  { method: 'GET', path: '/api/v1/sources/:id/replays/:replayId', params: ['replayId'] },
  { method: 'GET', path: '/api/v1/sources/:id/runs:errors', query: ['limit', 'include_archived'] },
  { method: 'GET', path: '/api/v1/sources/:id/schedules' },
  { method: 'GET', path: '/api/v1/sources/:id/schemas' },
  { method: 'GET', path: '/api/v1/sources/:id/schemas:drift-feed' },
  { method: 'GET', path: '/api/v1/sources/:id/slug-availability', query: ['slug'] },
  { method: 'GET', path: '/api/v1/sources/:id/storage-stats' },
  { method: 'GET', path: '/api/v1/sources/:id/triggers' },
  { method: 'GET', path: '/api/v1/sources/:id/triggers/:triggerId', params: ['triggerId'] },
  { method: 'GET', path: '/api/v1/sources/:id/triggers/:triggerId/rows', params: ['triggerId'] },
  { method: 'GET', path: '/api/v1/sources/:id/triggers/:triggerId/runs', params: ['triggerId'], query: ['limit'] },
  { method: 'GET', path: '/api/v1/sources/:id/triggers/:triggerId/table-count', params: ['triggerId'] },
  { method: 'GET', path: '/api/v1/sources/:id/unscoped-sources' },
  { method: 'GET', path: '/api/v1/sources/:id/usage' },
  { method: 'GET', path: '/api/v1/sources/:id/views' },
  { method: 'GET', path: '/api/v1/sources/:id/views/:viewId', params: ['viewId'] },
  { method: 'GET', path: '/api/v1/sources/:id/views:auto-suggest' },
  { method: 'GET', path: '/api/v1/sources/:id/views:health' },
  // ── POST ────────────────────────────────────────────────────────
  { method: 'POST', path: '/api/v1/sources/:id/auto-connect-bq', body: true },
  { method: 'POST', path: '/api/v1/sources/:id/bq-views', body: true },
  { method: 'POST', path: '/api/v1/sources/:id/bq-views:preview', body: true },
  { method: 'POST', path: '/api/v1/sources/:id/claim-unscoped', body: true },
  { method: 'POST', path: '/api/v1/sources/:id/erasure', body: true },
  { method: 'POST', path: '/api/v1/sources/:id/events/:eventId/reprocess', params: ['eventId'] },
  { method: 'POST', path: '/api/v1/sources/:id/inputs/:kind', params: ['kind'], body: true },
  { method: 'POST', path: '/api/v1/sources/:id/inputs/api_pull:fetch', body: true },
  { method: 'POST', path: '/api/v1/sources/:id/inputs/webhook:rotate-secret', body: true },
  { method: 'POST', path: '/api/v1/sources/:id/members', body: true },
  { method: 'POST', path: '/api/v1/sources/:id/notifications/:nid:ack', params: ['nid'] },
  { method: 'POST', path: '/api/v1/sources/:id/raw_uploads/:uploadId/reprocess', params: ['uploadId'] },
  { method: 'POST', path: '/api/v1/sources/:id/replays', body: true },
  { method: 'POST', path: '/api/v1/sources/:id/retention/purge', query: ['dry_run'] },
  { method: 'POST', path: '/api/v1/sources/:id/reveal', body: true },
  { method: 'POST', path: '/api/v1/sources/:id/runs/:run_id/archive', params: ['run_id'] },
  { method: 'POST', path: '/api/v1/sources/:id/runs/:run_id/unarchive', params: ['run_id'] },
  { method: 'POST', path: '/api/v1/sources/:id/schedules', body: true },
  { method: 'POST', path: '/api/v1/sources/:id/schedules/:scheduleId/run-now', params: ['scheduleId'] },
  { method: 'POST', path: '/api/v1/sources/:id/schema-drift:scan' },
  { method: 'POST', path: '/api/v1/sources/:id/schemas', body: true },
  { method: 'POST', path: '/api/v1/sources/:id/schemas/:schemaId/promote-to-bq', params: ['schemaId'], body: true },
  { method: 'POST', path: '/api/v1/sources/:id/select' },
  { method: 'POST', path: '/api/v1/sources/:id/triggers', body: true },
  { method: 'POST', path: '/api/v1/sources/:id/triggers:dry-run', body: true },
  { method: 'POST', path: '/api/v1/sources/:id/triggers:pause-all' },
  { method: 'POST', path: '/api/v1/sources/:id/views', body: true },
  { method: 'POST', path: '/api/v1/sources/:id/views:auto-name' },
  { method: 'POST', path: '/api/v1/sources/:id/views:propose-merges' },
  // ── PATCH ───────────────────────────────────────────────────────
  { method: 'PATCH', path: '/api/v1/sources/:id', body: true },
  { method: 'PATCH', path: '/api/v1/sources/:id/bq-views/:viewName', params: ['viewName'], body: true },
  { method: 'PATCH', path: '/api/v1/sources/:id/form-config', body: true },
  { method: 'PATCH', path: '/api/v1/sources/:id/inputs/:inputId', params: ['inputId'], body: true },
  { method: 'PATCH', path: '/api/v1/sources/:id/schedules/:scheduleId', params: ['scheduleId'], body: true },
  { method: 'PATCH', path: '/api/v1/sources/:id/schemas/:schemaId', params: ['schemaId'], body: true },
  { method: 'PATCH', path: '/api/v1/sources/:id/slug', body: true },
  { method: 'PATCH', path: '/api/v1/sources/:id/triggers/:triggerId', params: ['triggerId'], body: true },
  { method: 'PATCH', path: '/api/v1/sources/:id/views/:viewId', params: ['viewId'], body: true },
  // ── DELETE ──────────────────────────────────────────────────────
  { method: 'DELETE', path: '/api/v1/sources/:id' },
  { method: 'DELETE', path: '/api/v1/sources/:id/bq-views/:viewName', params: ['viewName'] },
  { method: 'DELETE', path: '/api/v1/sources/:id/schedules/:scheduleId', params: ['scheduleId'] },
  { method: 'DELETE', path: '/api/v1/sources/:id/schemas/:schemaId', params: ['schemaId'] },
  { method: 'DELETE', path: '/api/v1/sources/:id/triggers/:triggerId', params: ['triggerId'] },
  { method: 'DELETE', path: '/api/v1/sources/:id/views/:viewId', params: ['viewId'] },
];

// ── Page ──────────────────────────────────────────────────────────

export default function SourceDetailV4Page(): React.ReactElement | null {
  const { id } = useParams<{ id: string }>();
  const [name, setName] = useState<string>('');
  const [events, setEvents] = useState<DataHubEvent[]>([]);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [active, setActive] = useState<Endpoint | null>(null);
  const [schema, setSchema] = useState<SchemaField[]>([]);

  // Pinned column paths (B3 drill-in). Operator clicks "+col" inside
  // a NestedCell expand panel to promote a nested path (e.g.
  // "current.title") to a top-level column on this table. Persisted
  // in localStorage per source so the layout survives page reloads
  // without a backend round-trip. Cross-device sync via View.columns
  // will land when the FilterTree-view / BQ-view unification ships.
  const pinnedKey = id ? `v4-pinned-cols:${id}` : null;
  const [pinnedPaths, setPinnedPaths] = useState<string[]>(() => {
    if (typeof window === 'undefined' || !pinnedKey) return [];
    try {
      const raw = window.localStorage.getItem(pinnedKey);
      return raw ? (JSON.parse(raw) as string[]) : [];
    } catch { return []; }
  });
  const pinnedSet = useMemo(() => new Set(pinnedPaths), [pinnedPaths]);
  const togglePinnedPath = useCallback((path: string): void => {
    setPinnedPaths(prev => {
      const next = prev.includes(path) ? prev.filter(p => p !== path) : [...prev, path];
      if (pinnedKey && typeof window !== 'undefined') {
        try { window.localStorage.setItem(pinnedKey, JSON.stringify(next)); } catch { /* quota */ }
      }
      return next;
    });
  }, [pinnedKey]);

  // GET /:id → grab source name for the top bar.
  useEffect(() => {
    if (!id) return;
    fetch(`/api/v1/sources/${id}`)
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then((s: { name?: string }) => setName(s.name ?? ''))
      .catch(e => setError((e as Error).message));
  }, [id]);

  // GET /:id/events:preview → table content.
  useEffect(() => {
    if (!id) return;
    setLoadingEvents(true);
    fetch(`/api/v1/sources/${id}/events:preview?limit=200`)
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then((rows: DataHubEvent[]) => { setEvents(rows); setLoadingEvents(false); })
      .catch(e => { setError((e as Error).message); setLoadingEvents(false); });
  }, [id]);

  // GET /:id/events:schema → top-level path list to drive column headers.
  // Recursive shape (B1) — we only use depth=1 entries here; deeper
  // nesting is handled by NestedCell's per-row expand.
  useEffect(() => {
    if (!id) return;
    fetch(`/api/v1/sources/${id}/events:schema`)
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then((d: { fields?: SchemaField[] }) => setSchema(d.fields ?? []))
      .catch(() => undefined);
  }, [id]);

  if (!id) return null;

  return (
    <div style={pageStyle}>
      <header style={barStyle}>
        <span style={nameStyle}>{name || '…'}</span>
        <code style={idStyle}>{id}</code>
        <span style={{ flex: 1 }} />
        <ActionsMenu open={menuOpen} setOpen={setMenuOpen} onPick={ep => { setActive(ep); setMenuOpen(false); }} />
      </header>

      {error && <div style={errorBoxStyle}>{error}</div>}

      <EventsTable
        events={events}
        loading={loadingEvents}
        schema={schema}
        pinnedPaths={pinnedPaths}
        pinnedSet={pinnedSet}
        onTogglePinnedPath={togglePinnedPath}
      />

      {active && <RunEndpointModal endpoint={active} sourceId={id} onClose={() => setActive(null)} />}
    </div>
  );
}

// ── Actions menu ──────────────────────────────────────────────────

function ActionsMenu({
  open, setOpen, onPick,
}: {
  open: boolean;
  setOpen: (b: boolean) => void;
  onPick: (ep: Endpoint) => void;
}): React.ReactElement {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open, setOpen]);

  const grouped = useMemo(() => {
    const g: Record<Method, Endpoint[]> = { GET: [], POST: [], PATCH: [], DELETE: [] };
    ENDPOINTS.forEach(ep => g[ep.method].push(ep));
    (Object.keys(g) as Method[]).forEach(m => g[m].sort((a, b) => a.path.localeCompare(b.path)));
    return g;
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button type="button" onClick={() => setOpen(!open)} style={btnStyle}>
        Actions <span style={{ fontSize: 9, marginLeft: 4 }}>▾</span>
      </button>
      {open && (
        <div style={menuStyle}>
          {(['GET', 'POST', 'PATCH', 'DELETE'] as const).map(method => (
            <div key={method}>
              <div style={menuHeaderStyle}>{method}</div>
              {grouped[method].map(ep => (
                <button
                  key={`${ep.method} ${ep.path}`}
                  type="button"
                  onClick={() => onPick(ep)}
                  style={menuItemStyle}
                  title={ep.path}
                >
                  <span style={{ ...verbChipStyle, color: verbColor(method) }}>{method}</span>
                  <code style={{ fontFamily: 'ui-monospace, monospace', fontSize: 11.5 }}>
                    {ep.path.replace('/api/v1/sources/:id', '')}
                  </code>
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function verbColor(m: Method): string {
  if (m === 'GET') return '#0066cc';
  if (m === 'POST') return '#0a7f3f';
  if (m === 'PATCH') return '#a06900';
  return '#b32020'; // DELETE
}

// ── Run-endpoint modal ────────────────────────────────────────────

function RunEndpointModal({
  endpoint, sourceId, onClose,
}: {
  endpoint: Endpoint;
  sourceId: string;
  onClose: () => void;
}): React.ReactElement {
  const [pathVals, setPathVals] = useState<Record<string, string>>({});
  const [queryVals, setQueryVals] = useState<Record<string, string>>({});
  const [body, setBody] = useState<string>(endpoint.body ? '{\n}' : '');
  const [response, setResponse] = useState<{ status: number; text: string } | null>(null);
  const [running, setRunning] = useState(false);

  const needsForm = (endpoint.params && endpoint.params.length > 0)
    || (endpoint.body)
    || (endpoint.query && endpoint.query.length > 0);

  const run = useCallback(async (): Promise<void> => {
    setRunning(true);
    setResponse(null);
    try {
      let url = endpoint.path.replace(':id', sourceId);
      for (const p of endpoint.params ?? []) {
        url = url.replace(`:${p}`, encodeURIComponent(pathVals[p] ?? ''));
      }
      const qsParts: string[] = [];
      for (const k of endpoint.query ?? []) {
        const v = queryVals[k];
        if (v) qsParts.push(`${encodeURIComponent(k)}=${encodeURIComponent(v)}`);
      }
      if (qsParts.length > 0) url += `?${qsParts.join('&')}`;
      const init: RequestInit = { method: endpoint.method };
      if (endpoint.body && body.trim()) {
        init.headers = { 'Content-Type': 'application/json' };
        init.body = body;
      }
      const r = await fetch(url, init);
      const text = await r.text();
      setResponse({ status: r.status, text });
    } catch (e) {
      setResponse({ status: 0, text: `request failed: ${(e as Error).message}` });
    } finally { setRunning(false); }
  }, [endpoint, sourceId, pathVals, queryVals, body]);

  // GET with no params + no body + no query → run on open.
  useEffect(() => {
    if (!needsForm && endpoint.method === 'GET') void run();
  }, [needsForm, endpoint.method, run]);

  return (
    <div style={modalBackdropStyle} onClick={onClose}>
      <div style={modalStyle} onClick={e => e.stopPropagation()}>
        <header style={modalHeaderStyle}>
          <span style={{ ...verbChipStyle, color: verbColor(endpoint.method) }}>{endpoint.method}</span>
          <code style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>{endpoint.path}</code>
          <span style={{ flex: 1 }} />
          <button type="button" onClick={onClose} style={closeBtnStyle} aria-label="Close">×</button>
        </header>
        <div style={modalBodyStyle}>
          {(endpoint.params ?? []).map(p => (
            <label key={p} style={labelStyle}>
              <span style={labelTextStyle}>{p} (path)</span>
              <input
                value={pathVals[p] ?? ''}
                onChange={e => setPathVals({ ...pathVals, [p]: e.target.value })}
                style={inputStyle}
              />
            </label>
          ))}
          {(endpoint.query ?? []).map(q => (
            <label key={q} style={labelStyle}>
              <span style={labelTextStyle}>{q} (query, optional)</span>
              <input
                value={queryVals[q] ?? ''}
                onChange={e => setQueryVals({ ...queryVals, [q]: e.target.value })}
                style={inputStyle}
              />
            </label>
          ))}
          {endpoint.body && (
            <label style={labelStyle}>
              <span style={labelTextStyle}>body (JSON)</span>
              <textarea
                value={body}
                onChange={e => setBody(e.target.value)}
                rows={8}
                spellCheck={false}
                style={{ ...inputStyle, fontFamily: 'ui-monospace, monospace', fontSize: 12, resize: 'vertical' }}
              />
            </label>
          )}
          <button type="button" onClick={run} disabled={running} style={runBtnStyle}>
            {running ? 'Running…' : 'Run'}
          </button>
          {response && (
            <div style={{ marginTop: 10 }}>
              <div style={responseHeaderStyle}>
                status <strong>{response.status}</strong>
              </div>
              <pre style={responseBodyStyle}>{response.text}</pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Events table ──────────────────────────────────────────────────

function EventsTable({
  events, loading, schema, pinnedPaths, pinnedSet, onTogglePinnedPath,
}: {
  events: DataHubEvent[];
  loading: boolean;
  schema: SchemaField[];
  pinnedPaths: string[];
  pinnedSet: Set<string>;
  onTogglePinnedPath: (path: string) => void;
}): React.ReactElement {
  // Derive payload columns from the inferred schema: top-level paths
  // (depth=1, no parent). Sort by occurrences desc so the most-common
  // payload keys appear leftmost. Capped at 12 columns to keep the
  // table scannable; rest fold into an overflow "+N more" header note.
  const payloadCols = useMemo(() => {
    return schema
      .filter(f => f.depth === 1 && !f.parent)
      .slice(0, 12);
  }, [schema]);
  const overflowCols = Math.max(0, schema.filter(f => f.depth === 1 && !f.parent).length - payloadCols.length);

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>Loading events…</div>;
  if (events.length === 0) return <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>No events.</div>;
  return (
    <div style={tableWrapStyle}>
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={thStyle}>received_at</th>
            <th style={thStyle}>event_id</th>
            <th style={thStyle}>origin</th>
            <th style={thStyle}>input tags</th>
            {payloadCols.map(col => (
              <th key={col.path} style={thStyle} title={`${col.occurrences}/${events.length} events have this path${col.collapsed_cardinality ? ` · ${col.collapsed_cardinality} children collapsed (drill in to expand)` : ''}${col.sparse ? ' · sparse' : ''}`}>
                {col.path}
                {col.collapsed_cardinality && <span style={{ marginLeft: 4, color: '#888', fontWeight: 400 }}>·{col.collapsed_cardinality}</span>}
                {col.sparse && <span style={{ marginLeft: 4, color: '#888', fontWeight: 400 }}>·sparse</span>}
              </th>
            ))}
            {overflowCols > 0 && <th style={{ ...thStyle, color: '#888' }} title="Additional top-level keys not shown (cap = 12)">+{overflowCols}</th>}
            {pinnedPaths.map(p => (
              <th key={p} style={{ ...thStyle, background: 'oklch(0.96 0.04 235 / 0.5)' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <code style={{ fontFamily: 'ui-monospace, monospace', fontSize: 10.5, color: '#0066cc' }}>{p}</code>
                  <button
                    type="button"
                    onClick={() => onTogglePinnedPath(p)}
                    aria-label={`Unpin column ${p}`}
                    title={`Unpin ${p}`}
                    style={{
                      padding: '0 4px', fontSize: 11, lineHeight: 1,
                      background: 'transparent', border: '1px solid #ccc',
                      borderRadius: 3, color: '#666', cursor: 'pointer',
                    }}
                  >×</button>
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {events.map(e => {
            const inputTags = ((e.origin as { input_tags?: Record<string, string> })?.input_tags) ?? {};
            const payload = (e.payload ?? {}) as Record<string, unknown>;
            return (
              <tr key={e.event_id} style={trStyle}>
                <td style={tdStyle}>{new Date(e.received_at).toISOString().replace('T', ' ').slice(0, 19)}</td>
                <td style={{ ...tdStyle, fontFamily: 'ui-monospace, monospace' }}>{e.event_id}</td>
                <td style={{ ...tdStyle, fontFamily: 'ui-monospace, monospace' }}>{e.origin?.kind ?? '—'}</td>
                <td style={tdStyle}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {Object.entries(inputTags).map(([k, v]) => (
                      <TagChip key={k} k={k} v={v} compact />
                    ))}
                  </div>
                </td>
                {payloadCols.map(col => (
                  <td key={col.path} style={{ ...tdStyle, verticalAlign: 'top' }}>
                    <NestedCell
                      value={payload[col.path]}
                      collapsedCardinality={col.collapsed_cardinality}
                      compact
                      pathPrefix={col.path}
                      onPinPath={onTogglePinnedPath}
                      pinnedPaths={pinnedSet}
                    />
                  </td>
                ))}
                {overflowCols > 0 && <td style={tdStyle}>—</td>}
                {pinnedPaths.map(p => (
                  <td key={p} style={{ ...tdStyle, verticalAlign: 'top', background: 'oklch(0.98 0.02 235 / 0.3)' }}>
                    <NestedCell value={readPath(payload, p)} compact />
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────

const FONT = '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif';
const BORDER = '#d8d8d8';
const INK = '#111';
const INK2 = '#555';

const pageStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'column',
  minHeight: '100vh',
  fontFamily: FONT,
  color: INK,
  background: '#fff',
};
const barStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 10,
  padding: '8px 16px',
  borderBottom: `1px solid ${BORDER}`,
  fontSize: 13,
};
const nameStyle: React.CSSProperties = { fontWeight: 600 };
const idStyle: React.CSSProperties = { fontFamily: 'ui-monospace, monospace', fontSize: 11.5, color: INK2 };
const btnStyle: React.CSSProperties = {
  padding: '4px 12px', fontSize: 12, fontFamily: FONT,
  background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 3,
  cursor: 'pointer', color: INK,
};
const menuStyle: React.CSSProperties = {
  position: 'absolute', top: '100%', right: 0, marginTop: 4,
  width: 480, maxHeight: 480, overflowY: 'auto',
  background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 3,
  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
  zIndex: 10,
};
const menuHeaderStyle: React.CSSProperties = {
  padding: '6px 10px', fontSize: 10.5, fontFamily: 'ui-monospace, monospace',
  textTransform: 'uppercase', letterSpacing: '0.08em',
  background: '#f7f7f7', borderBottom: `1px solid ${BORDER}`, color: INK2,
  position: 'sticky', top: 0,
};
const menuItemStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8,
  width: '100%', padding: '4px 10px',
  background: '#fff', border: 'none', borderBottom: `1px solid #f0f0f0`,
  cursor: 'pointer', textAlign: 'left',
};
const verbChipStyle: React.CSSProperties = {
  fontFamily: 'ui-monospace, monospace', fontSize: 10,
  fontWeight: 600, minWidth: 44, display: 'inline-block',
};
const errorBoxStyle: React.CSSProperties = {
  padding: '8px 16px', fontSize: 12, color: '#b32020',
  background: '#fff5f5', borderBottom: `1px solid #f0c0c0`,
};
const tableWrapStyle: React.CSSProperties = { flex: 1, overflow: 'auto' };
const tableStyle: React.CSSProperties = {
  width: '100%', borderCollapse: 'collapse', fontSize: 12,
};
const thStyle: React.CSSProperties = {
  textAlign: 'left', padding: '8px 12px',
  fontWeight: 600, fontSize: 11, fontFamily: 'ui-monospace, monospace',
  textTransform: 'uppercase', letterSpacing: '0.06em',
  color: INK2, borderBottom: `1px solid ${BORDER}`,
  position: 'sticky', top: 0, background: '#fff',
};
const trStyle: React.CSSProperties = { borderBottom: `1px solid #f0f0f0` };
const tdStyle: React.CSSProperties = { padding: '6px 12px', verticalAlign: 'top' };

const modalBackdropStyle: React.CSSProperties = {
  position: 'fixed', inset: 0,
  background: 'rgba(0,0,0,0.3)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 100,
};
const modalStyle: React.CSSProperties = {
  background: '#fff', borderRadius: 4,
  width: 'min(720px, 92vw)', maxHeight: '88vh',
  display: 'flex', flexDirection: 'column',
  boxShadow: '0 16px 48px rgba(0,0,0,0.18)',
};
const modalHeaderStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 10,
  padding: '10px 16px', borderBottom: `1px solid ${BORDER}`,
};
const modalBodyStyle: React.CSSProperties = {
  padding: 16, overflowY: 'auto',
  display: 'flex', flexDirection: 'column', gap: 10,
};
const closeBtnStyle: React.CSSProperties = {
  background: 'transparent', border: 'none', cursor: 'pointer',
  fontSize: 22, lineHeight: 1, color: INK2, padding: 0,
};
const labelStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4 };
const labelTextStyle: React.CSSProperties = {
  fontSize: 10.5, fontFamily: 'ui-monospace, monospace',
  textTransform: 'uppercase', letterSpacing: '0.06em', color: INK2,
};
const inputStyle: React.CSSProperties = {
  padding: '6px 10px', fontSize: 13, fontFamily: FONT,
  border: `1px solid ${BORDER}`, borderRadius: 3, background: '#fff', color: INK,
};
const runBtnStyle: React.CSSProperties = {
  alignSelf: 'flex-start',
  padding: '6px 14px', fontSize: 13,
  background: '#111', color: '#fff', border: 'none', borderRadius: 3,
  cursor: 'pointer',
};
const responseHeaderStyle: React.CSSProperties = {
  fontSize: 10.5, fontFamily: 'ui-monospace, monospace',
  textTransform: 'uppercase', letterSpacing: '0.06em', color: INK2,
  marginBottom: 4,
};
const responseBodyStyle: React.CSSProperties = {
  margin: 0, padding: '10px 12px', fontSize: 11.5,
  background: '#f7f7f7', border: `1px solid ${BORDER}`, borderRadius: 3,
  fontFamily: 'ui-monospace, monospace', color: INK,
  whiteSpace: 'pre-wrap', wordBreak: 'break-word',
  maxHeight: 320, overflowY: 'auto',
};
